import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon";

function cameraError(caught: unknown): string {
  const name = caught instanceof DOMException ? caught.name : "";
  if (name === "NotAllowedError" || name === "SecurityError")
    return "Camera access is blocked for this site. Allow it from the camera icon in the browser's address bar, then try again.";
  if (name === "NotFoundError" || name === "OverconstrainedError")
    return "No camera was found on this computer.";
  if (name === "NotReadableError")
    return "The camera is in use by another app, such as Teams. Close it there and try again.";
  return "The camera couldn't be started.";
}

/**
 * A live camera for computers, where a file input only opens a file picker. Each photo is handed
 * over as it's taken, and the camera stays on for the next, so a stack of receipts goes quickly.
 * The camera is switched off when this closes.
 */
export function CameraCapture({
  onPhoto,
  onChooseFile,
}: {
  onPhoto: (file: File) => void;
  onChooseFile: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>();
  const [error, setError] = useState<string>();
  const [live, setLive] = useState(false);
  const [taken, setTaken] = useState(0);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    let stream: MediaStream | undefined;
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({
        audio: false,
        video: deviceId
          ? { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
          : {
              facingMode: { ideal: "environment" },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
      })
      .then(async (opened) => {
        if (cancelled) {
          opened.getTracks().forEach((track) => track.stop());
          return;
        }
        stream = opened;
        if (video.current) {
          video.current.srcObject = opened;
          await video.current.play().catch(() => undefined);
        }
        setLive(true);
        // Names are only listed once the camera is allowed.
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!cancelled) setCameras(devices.filter((d) => d.kind === "videoinput"));
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(cameraError(caught));
      });
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [deviceId]);

  function take() {
    const source = video.current;
    if (!source || source.videoWidth === 0) return;
    const canvas = document.createElement("canvas");
    canvas.width = source.videoWidth;
    canvas.height = source.videoHeight;
    canvas.getContext("2d")?.drawImage(source, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const number = taken + 1;
        setTaken(number);
        onPhoto(new File([blob], `Receipt photo ${String(number)}.jpg`, { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92,
    );
    setFlash(true);
    window.setTimeout(() => setFlash(false), 180);
  }

  if (error) {
    return (
      <div className="stack">
        <div className="error" role="alert">
          {error}
        </div>
        <button type="button" className="btn secondary" onClick={onChooseFile}>
          <Icon name="upload" size={16} /> Choose a file instead
        </button>
      </div>
    );
  }

  return (
    <div className="stack camera">
      <div className={`camera-view ${flash ? "flash" : ""}`}>
        <video ref={video} playsInline muted aria-label="Camera preview" />
        {!live && <span className="camera-starting">Starting the camera…</span>}
      </div>
      <div className="note camera-tip">
        <strong>Works better from your phone</strong>
        <span>
          A phone camera focuses on small print and handles light better than a computer&apos;s.
          Open PA Expenses on your phone to snap receipts there.
        </span>
      </div>
      <p className="muted small">
        Hold the receipt flat and fill the frame. Take as many as you like.
      </p>
      {cameras.length > 1 && (
        <label>
          Camera
          <select
            value={deviceId ?? ""}
            onChange={(e) => {
              setLive(false);
              setDeviceId(e.target.value === "" ? undefined : e.target.value);
            }}
          >
            <option value="">Default</option>
            {cameras.map((camera, index) => (
              <option key={camera.deviceId} value={camera.deviceId}>
                {camera.label || `Camera ${String(index + 1)}`}
              </option>
            ))}
          </select>
        </label>
      )}
      <button type="button" className="btn" onClick={take} disabled={!live}>
        <Icon name="camera" size={18} /> Take photo
      </button>
      {taken > 0 && (
        <p className="success" role="status">
          {taken === 1 ? "1 photo added" : `${String(taken)} photos added`}. They're being read on
          the report.
        </p>
      )}
    </div>
  );
}
